import React, { useState, useRef } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Eye, EyeOff, UserCircle2, Users, GraduationCap, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store/authstore';
import type { AuthState } from '@/store/authstore';
import { RoleType } from '@/types/RoleType';
import { ROUTES } from '@/router/routes';

const ROLES: { id: RoleType; label: string; Icon: React.FC<{ size?: number }> }[] = [
  { id: 'admin', label: 'Admin', Icon: UserCircle2 },
  { id: 'employee', label: 'Employee', Icon: Users },
  { id: 'student', label: 'Student', Icon: GraduationCap },
];

const inputSx = {
  width: '100%',
  display: 'block',
  bgcolor: '#F3F4FA',
  border: '1.5px solid #E0E3F0',
  borderRadius: '10px',
  color: '#0F172A',
  fontSize: '14px',
  fontFamily: '"DM Sans", sans-serif',
  outline: 'none',
  padding: '12px 16px',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  '&:focus': {
    borderColor: '#7C63D8',
    boxShadow: '0 0 0 3px rgba(124,99,216,0.15)',
  },
  '&::placeholder': { color: '#9BA5C0' },
} as const;

export default function LoginCard() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const pendingPasswordEntry = useAuthStore((s) => s.pendingPasswordEntry);

  const [role, setRole] = useState<RoleType>('admin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ username?: string; password?: string }>({});
  const submittingRef = useRef(false);

  const isStep2 = !!pendingPasswordEntry;
  const isStudentStep1 = role === 'student' && !isStep2;

  const handleRoleChange = (newRole: RoleType) => {
    setRole(newRole);
    setUsername('');
    setPassword('');
    setErrors({});
    useAuthStore.setState({ pendingPasswordEntry: null });
  };

  const validate = () => {
    const e: { username?: string; password?: string } = {};
    if (!isStep2 && !username.trim()) e.username = 'This field is required';
    if (!isStudentStep1 && !password.trim()) e.password = 'Password is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!validate()) return;
    submittingRef.current = true;
    setLoading(true);
    try {
      if (isStep2) {
        await login(pendingPasswordEntry.username, password.trim(), pendingPasswordEntry.roleType as RoleType);
      } else {
        await login(username.trim(), isStudentStep1 ? '' : password.trim(), role);
      }

      const state = useAuthStore.getState() as AuthState;

      // pendingPasswordEntry set → step 2, reset guard so step 2 can submit
      if (state.pendingPasswordEntry) {
        submittingRef.current = false;
        return;
      }

      if (state.pendingPasswordSetup) {
        navigate(ROUTES.AUTH.SET_PASSWORD, { state: { challengeToken: state.pendingPasswordSetup.challengeToken } });
      } else if (state.pendingPhoneVerification) {
        navigate(ROUTES.AUTH.VERIFY_PHONE);
      } else if (state.pendingPasswordSetup) {
        navigate(ROUTES.AUTH.SET_PASSWORD, { state: { challengeToken: state.pendingPasswordSetup.challengeToken } });
      } else if (state.pendingProfileSelection) {
        navigate(ROUTES.AUTH.SELECT_PROFILE);
      } else if (state.session?.user.roleType === 'student' && !state.session?.user.isSubscribed) {
        navigate(ROUTES.AUTH.SUBSCRIBE);
      } else {
        toast.success('Login successful!');
        navigate('/dashboard');
      }
    } catch (err) {
      submittingRef.current = false;
      toast.error(
        typeof err === 'object' && err !== null && 'message' in err
          ? (err as { message: string }).message
          : 'Login failed. Please check your credentials.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      bgcolor: '#FFFFFF',
      border: '1px solid rgba(124,99,216,0.12)',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      p: { xs: '28px', md: '36px' },
      width: '100%',
    }}>

      {/* Header */}
      <Box sx={{ mb: 3.5, textAlign: 'center' }}>
        <Typography sx={{
          fontFamily: '"Syne", sans-serif', fontSize: '22px',
          fontWeight: 800, color: '#0F172A', mb: 0.75, letterSpacing: '-0.3px',
        }}>
          Welcome Back
        </Typography>
        <Typography sx={{
          fontSize: '13.5px', color: '#64748B',
          fontWeight: 400, lineHeight: 1.5,
          letterSpacing: '0.01em',
        }}>
          Sign in to access your account
        </Typography>
      </Box>

      {/* Role Selector — hidden in step 2 */}
      {!isStep2 && <Box sx={{
        display: 'flex', justifyContent: 'center', gap: { xs: 3, md: 4 },
        mb: 3.5, px: 1,
      }}>
        {ROLES.map(({ id, label, Icon }) => {
          const active = role === id;
          return (
            <Box
              key={id}
              onClick={() => handleRoleChange(id)}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
            >
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: active ? '#7C63D8' : '#F3F4FA',
                border: active ? '2px solid #7C63D8' : '2px solid #E0E3F0',
                boxShadow: active ? '0 4px 16px rgba(124,99,216,0.35)' : 'none',
                transition: 'all 0.2s ease',
                color: active ? '#fff' : '#8B95C0',
                '&:hover': {
                  bgcolor: active ? '#7C63D8' : 'rgba(124,99,216,0.08)',
                  borderColor: active ? '#7C63D8' : 'rgba(124,99,216,0.35)',
                  color: active ? '#fff' : '#7C63D8',
                },
              }}>
                <Icon size={22} />
              </Box>
              <Typography sx={{
                fontSize: '11.5px', fontWeight: active ? 600 : 400,
                color: active ? '#7C63D8' : '#9BA5C0',
                fontFamily: '"DM Sans", sans-serif',
                transition: 'color 0.2s',
                userSelect: 'none',
              }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>}

      {/* Form */}
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Step 2: show username read-only */}
        {isStep2 && (
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748B', mb: 1 }}>
              Signing in as
            </Typography>
            <Box sx={{ bgcolor: '#F3F4FA', border: '1.5px solid #E0E3F0', borderRadius: '10px', px: 2, py: 1.5 }}>
              <Typography sx={{ fontWeight: 600, color: '#0F172A', fontSize: '14px' }}>
                {pendingPasswordEntry.username}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Username / Phone — hidden in step 2 */}
        {!isStep2 && (
          <Box>
            <Typography sx={{
              fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: '#64748B', mb: 1,
            }}>
              {role === 'student' ? 'Phone Number' : 'Username or Email'}
            </Typography>
            <Box
              component="input"
              type={role === 'student' ? 'tel' : 'text'}
              value={username}
              autoFocus
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setUsername(e.target.value);
                setErrors(prev => ({ ...prev, username: undefined }));
              }}
              placeholder={role === 'student' ? '+91 98765 43210' : 'Enter your username or email'}
              sx={{
                ...inputSx,
                borderColor: errors.username ? 'rgba(248,113,113,0.5)' : 'rgba(255,255,255,0.12)',
              }}
            />
            {errors.username && (
              <Typography sx={{ fontSize: '12px', color: '#F87171', mt: 0.75 }}>
                {errors.username}
              </Typography>
            )}
          </Box>
        )}

        {/* Password — hidden for student step 1 */}
        {!isStudentStep1 && (
          <Box>
            <Typography sx={{
              fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: '#64748B', mb: 1,
            }}>
              Password
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <Box
                component="input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                autoFocus={isStep2}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPassword(e.target.value);
                  setErrors(prev => ({ ...prev, password: undefined }));
                }}
                placeholder="Enter your password"
                sx={{
                  ...inputSx,
                  paddingRight: '48px',
                  borderColor: errors.password ? 'rgba(248,113,113,0.5)' : 'rgba(255,255,255,0.12)',
                }}
              />
              <Box
                onClick={() => setShowPassword(p => !p)}
                sx={{
                  position: 'absolute', right: '14px', top: '50%',
                  transform: 'translateY(-50%)', cursor: 'pointer',
                  color: '#9BA5C0', display: 'flex', alignItems: 'center',
                  '&:hover': { color: '#7C63D8' }, transition: 'color 0.2s',
                }}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </Box>
            </Box>
            {errors.password && (
              <Typography sx={{ fontSize: '12px', color: '#F87171', mt: 0.75 }}>
                {errors.password}
              </Typography>
            )}
          </Box>
        )}

        {/* Submit */}
        <Button
          type="submit"
          fullWidth
          disabled={loading}
          sx={{
            mt: 0.5, py: '13px', bgcolor: '#7C63D8', color: '#fff',
            fontSize: '15px', fontWeight: 600, borderRadius: '10px',
            boxShadow: '0 4px 16px rgba(124,99,216,0.35)',
            '&:hover': { bgcolor: '#6B52D1', boxShadow: '0 6px 20px rgba(124,99,216,0.5)', transform: 'translateY(-1px)' },
            '&:disabled': { bgcolor: 'rgba(124,99,216,0.5)', color: 'rgba(255,255,255,0.7)', transform: 'none' },
            transition: 'all 0.2s',
          }}
        >
          {loading
            ? <CircularProgress size={18} sx={{ color: 'rgba(255,255,255,0.8)' }} />
            : isStudentStep1 ? 'Continue' : 'Sign In'}
        </Button>

        {/* Bottom row: back / forgot */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isStep2 ? (
            <Box
              component="button"
              type="button"
              onClick={() => useAuthStore.setState({ pendingPasswordEntry: null })}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '13px', color: '#7C63D8', fontWeight: 500, p: 0,
                '&:hover': { color: '#6B52D1' }, transition: 'color 0.2s',
              }}
            >
              <ArrowLeft size={14} /> Back
            </Box>
          ) : (
            <Box />
          )}
          <Box
            component="a"
            href={ROUTES.AUTH.FORGOT_PASSWORD}
            sx={{
              fontSize: '13px', color: '#7C63D8', textDecoration: 'none', fontWeight: 500,
              '&:hover': { color: '#6B52D1' }, transition: 'color 0.2s',
            }}
          >
            Forgot Password?
          </Box>
        </Box>

      </Box>
    </Box>
  );
}
